import { useLoaderData, useNavigate, Form } from 'react-router';
import { requireUser } from '../../../lib/session.server';
import { resumeApi } from '../../../lib/api';
import type { LoaderFunctionArgs, ActionFunctionArgs } from 'react-router';
import { FileText, Upload, Trash2, Star, Calendar, Plus, Download, Edit, Play, AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { Card, Button, Badge } from '../../../components/ui';
import { ResumeStatusChip, useToastActions } from '../../../components/ui';
import type { Resume, User } from '../../../types';

interface ResumesData {
  user: User;
  resumes: Resume[];
}

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await requireUser(request);

  try {
    const resumes = await resumeApi.getResumes(user.id);
    
    return {
      user,
      resumes,
    };
  } catch (error) {
    return {
      user,
      resumes: [],
      error: 'Failed to load resumes',
    };
  }
}

export async function action({ request }: ActionFunctionArgs) {
  const user = await requireUser(request);
  const formData = await request.formData();
  const intent = formData.get('intent') as string;
  
  try {
    switch (intent) {
      case 'upload': {
        const file = formData.get('file') as File;
        const makeActive = formData.get('makeActive') === 'true';
        
        if (file) {
          // Validate user has permission to upload
          if (!user.id) {
            return { success: false, error: 'Authentication required' };
          }
          await resumeApi.uploadResume(file, { make_active: makeActive });
          return { success: true, message: 'Resume uploaded successfully' };
        }
        
        return { success: false, error: 'No file selected' };
      }
      
      case 'delete': {
        const resumeId = formData.get('resumeId') as string;
        // Verify user owns this resume before deletion
        if (!user.id) {
          return { success: false, error: 'Authentication required' };
        }
        await resumeApi.deleteResume(resumeId);
        return { success: true, message: 'Resume deleted' };
      }
      
      case 'setActive': {
        const resumeId = formData.get('resumeId') as string;
        // Verify user can set active resume
        if (!user.id) {
          return { success: false, error: 'Authentication required' };
        }
        await resumeApi.setActiveResume(resumeId);
        return { success: true, message: 'Active resume updated' };
      }
      
      case 'render': {
        const resumeId = formData.get('resumeId') as string;
        const jobResponse = await resumeApi.renderResume(resumeId);
        return { success: true, message: 'PDF rendering started', jobId: jobResponse.job_id };
      }
      
      case 'reparse': {
        const resumeId = formData.get('resumeId') as string;
        // Call the reparse endpoint
        const response = await fetch(`https://api.nduka.io/api/v2/resumes/${resumeId}/reparse`, {
          method: 'POST',
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Reparse request failed');
        }
        
        return { success: true, message: 'Resume parsing restarted' };
      }
      
      default:
        return { success: false, error: 'Invalid action' };
    }
  } catch (error) {
    return { success: false, error: 'Action failed' };
  }
}

export function ResumesPage() {
  const { user, resumes } = useLoaderData() as ResumesData;
  const navigate = useNavigate();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { success, error } = useToastActions();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.includes('pdf') && !file.type.includes('word')) {
        error('Invalid File Type', 'Please select a PDF or Word document.');
        return;
      }
      
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        error('File Too Large', 'File must be less than 10MB.');
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setIsUploading(true);
    try {
      // Use the proper API client
      await resumeApi.uploadResume(selectedFile, { 
        make_active: resumes.length === 0 
      });
      
      success('Resume Uploaded', 'Your resume has been uploaded and processing has started.');
      setShowUploadModal(false);
      setSelectedFile(null);
      window.location.reload(); // Refresh to show new resume
    } catch (err: any) {
      console.error('Upload error:', err);
      error('Upload Failed', err.message || 'Unable to upload resume. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadOriginal = async (resume: Resume) => {
    try {
      const blob = await resumeApi.downloadOriginal(resume.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = resume.filename || `resume-${resume.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      error('Download Failed', 'Unable to download resume.');
    }
  };

  const handleDownloadGenerated = async (resume: Resume) => {
    try {
      const blob = await resumeApi.downloadGenerated(resume.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `resume-generated-${resume.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      error('Download Failed', 'Generated PDF not available or download failed.');
    }
  };

  const handleRender = async (resumeId: string) => {
    try {
      const formData = new FormData();
      formData.append('intent', 'render');
      formData.append('resumeId', resumeId);
      
      const response = await fetch('', {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        success('PDF Rendering Started', 'Your resume PDF is being generated.');
      } else {
        throw new Error('Render failed');
      }
    } catch (err) {
      error('Render Failed', 'Unable to start PDF rendering.');
    }
  };

  const handleRetryParse = async (resumeId: string) => {
    try {
      const formData = new FormData();
      formData.append('intent', 'reparse');
      formData.append('resumeId', resumeId);
      
      const response = await fetch('', {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        success('Parse Retry Started', 'Retrying resume parsing...');
        // Refresh the page to show updated status
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        throw new Error('Retry failed');
      }
    } catch (err) {
      error('Retry Failed', 'Unable to retry parsing. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-6 w-6 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Resume Library</h1>
        </div>
        <p className="text-muted-foreground">
          Welcome back, {user.firstName}! Manage your resumes with AI-powered parsing, tagging, and PDF generation.
        </p>
      </div>

      {/* Action Bar */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          {resumes.length} resume{resumes.length !== 1 ? 's' : ''} in your library
        </div>
        <Button onClick={() => setShowUploadModal(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Upload Resume
        </Button>
      </div>

      {/* Resume Grid */}
      {resumes.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No resumes uploaded</h3>
            <p className="text-muted-foreground mb-4">
              Upload your first resume to get AI-powered parsing and tagging.
            </p>
            <Button onClick={() => setShowUploadModal(true)} className="gap-2">
              <Upload className="h-4 w-4" />
              Upload Resume
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {resumes.map((resume) => (
            <Card key={resume.id} className={`transition-all hover:shadow-lg ${resume.is_active ? 'ring-2 ring-primary/30' : ''}`}>
              <div className="p-6 space-y-5">
                {/* Header */}
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="p-3 bg-primary/10 rounded-xl">
                        <FileText className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground text-base leading-tight truncate" title={resume.original_filename}>
                          {resume.original_filename}
                        </h3>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-sm text-muted-foreground">
                            {formatFileSize(resume.file_size)}
                          </span>
                          {resume.page_count && (
                            <>
                              <span className="text-muted-foreground">•</span>
                              <span className="text-sm text-muted-foreground">
                                {resume.page_count} page{resume.page_count !== 1 ? 's' : ''}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {resume.is_active && (
                      <Badge variant="primary" className="gap-1 shrink-0">
                        <Star className="h-3 w-3" />
                        Active
                      </Badge>
                    )}
                  </div>
                  
                  {/* Upload Date */}
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    Uploaded {formatDate(resume.upload_date)}
                  </div>
                </div>

                {/* Status Indicators */}
                <div className="space-y-2">
                  <div className="text-xs font-medium text-foreground">Processing Status</div>
                  <div className="flex flex-wrap gap-2">
                    <ResumeStatusChip
                      status={resume.status?.parsed ? 'completed' : resume.status?.parse_status || 'pending'}
                      type="parse"
                    />
                    <ResumeStatusChip
                      status={resume.status?.tagged ? 'completed' : resume.status?.tag_status || 'pending'}
                      type="tag"
                    />
                    <ResumeStatusChip
                      status={resume.status?.rendered ? 'completed' : resume.status?.render_status || 'pending'}
                      type="render"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-3 pt-2 border-t border-border">
                  {/* Primary Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => navigate(`/resumes/${resume.id}`)}
                      className="flex-1 gap-2"
                      disabled={!resume.status?.parsed}
                    >
                      <Edit className="h-4 w-4" />
                      Edit Text
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadOriginal(resume)}
                      className="gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Original
                    </Button>
                  </div>

                  {/* Secondary Actions */}
                  <div className="flex gap-2">
                    {resume.status?.rendered ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadGenerated(resume)}
                        className="flex-1 gap-2"
                      >
                        <Download className="h-4 w-4" />
                        Generated PDF
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRender(resume.id)}
                        disabled={!resume.status?.parsed}
                        className="flex-1 gap-2"
                      >
                        <Play className="h-4 w-4" />
                        Generate PDF
                      </Button>
                    )}

                    <div className="flex gap-1">
                      {!resume.is_active && (
                        <Form method="post">
                          <input type="hidden" name="intent" value="setActive" />
                          <input type="hidden" name="resumeId" value={resume.id} />
                          <Button type="submit" variant="ghost" size="sm" title="Set as active resume">
                            <Star className="h-4 w-4" />
                          </Button>
                        </Form>
                      )}

                      <Form method="post">
                        <input type="hidden" name="intent" value="delete" />
                        <input type="hidden" name="resumeId" value={resume.id} />
                        <Button
                          type="submit"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            if (!confirm('Are you sure you want to delete this resume?')) {
                              e.preventDefault();
                            }
                          }}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          title="Delete resume"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </Form>
                    </div>
                  </div>
                </div>

                {/* Error State with Retry */}
                {(resume.status?.parse_status === 'failed' || 
                  resume.status?.tag_status === 'failed' || 
                  resume.status?.render_status === 'failed') && (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div className="text-sm">
                            <p className="font-medium text-destructive mb-1">Processing Error</p>
                            <p className="text-destructive/80 text-xs">
                              {resume.status?.last_error || 'One or more processing steps failed.'}
                            </p>
                          </div>
                          {resume.status?.parse_status === 'failed' && (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleRetryParse(resume.id)}
                              className="ml-2 text-xs px-2 py-1 h-auto"
                            >
                              Retry
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <Card className="max-w-md w-full mx-4">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Upload Resume</h3>
                <p className="text-sm text-muted-foreground">
                  Upload a PDF or Word document for AI processing.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Select File
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.docx"
                    onChange={handleFileSelect}
                    className="w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                  />
                  {selectedFile && (
                    <div className="mt-2 text-sm text-muted-foreground">
                      Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                    </div>
                  )}
                </div>

                <div className="text-xs text-muted-foreground bg-muted/30 rounded-xl p-3">
                  <p>
                    <strong>Processing Pipeline:</strong> Your resume will be parsed for text, 
                    tagged with AI-generated skills, and made available for PDF generation.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={!selectedFile || isUploading}
                  className="flex-1"
                >
                  {isUploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}